import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, Chip, FormControlLabel, Grid, InputAdornment, MenuItem, Stack, Switch,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { CheckCircle, Cancel, Lock, RestartAlt } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import FormFileUpload from '../ui/forms/FormFileUpload';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getCountryReference,
  getMasters,
  type CountryReference,
  type CountryReferenceBundle,
} from '../../services/platform/masterService';
import {
  COUNTRY_FIELD_LABELS,
  UNITS_BY_SYSTEM,
  countryConsistencyWarnings,
  nextCountrySortOrder,
  parseJsonArray,
  parseJsonObject,
  validateCountryForm,
  type CountryExistingRecord,
  type CountryMeta,
} from '../../utils/countryMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  existingRecords?: CountryExistingRecord[];
  editingId?: string;
  saveError?: string | null;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ADDRESS_TOKEN_LABELS: Record<string, string> = {
  line1: 'Address line 1',
  line2: 'Address line 2',
  landmark: 'Landmark',
  area: 'Area / Locality',
  city: 'City',
  district: 'District',
  state: 'State / Province',
  postalCode: 'Postal code',
  country: 'Country',
};
const TAX_LABELS: Record<string, string> = { gst: 'GST', vat: 'VAT', sales_tax: 'Sales Tax', none: 'None' };

const str = (v: unknown) => (v == null ? '' : String(v).trim());

function currencyName(code: string) {
  try {
    return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || code;
  } catch {
    return code;
  }
}

function timezoneOffset(tz: string) {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value || '';
  } catch {
    return '';
  }
}

function formatDms(value: number, positive: string, negative: string) {
  const abs = Math.abs(value);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = ((minFloat - min) * 60).toFixed(1);
  return `${deg}°${min}′${sec}″${value >= 0 ? positive : negative}`;
}

function datePreview(format: string) {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  return format.replace('DD', dd).replace('MM', mm).replace('YYYY', yyyy);
}

function numberPreview(locale: string) {
  try {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).format(1234567.89);
  } catch {
    return '';
  }
}

/** Full locale configuration for a country, taken from ISO reference data and country defaults. */
function metadataFromReference(ref: CountryReference): CountryMeta {
  const d = ref.defaults;
  const otherLanguages = ref.languages.slice(1);
  return {
    iso3Code: ref.iso3,
    phoneCode: ref.phoneCode,
    nationality: d.nationality,
    defaultCurrency: ref.currencies[0] || '',
    secondaryCurrency: ref.currencies[1] || '',
    defaultLanguage: ref.languages[0] || 'en',
    additionalLanguages: JSON.stringify(otherLanguages),
    timezone: ref.timezone,
    weekendDays: JSON.stringify(d.weekendDays),
    firstDayOfWeek: d.firstDayOfWeek,
    dateFormat: d.dateFormat,
    timeFormat: d.timeFormat,
    numberFormat: d.numberFormat,
    taxSystem: d.taxSystem,
    gstVatEnabled: d.gstVatEnabled,
    invoicePrefix: d.invoicePrefix,
    postalCodePattern: d.postalCodePattern,
    addressFormat: JSON.stringify(d.addressFormat),
    measurementSystem: d.measurementSystem,
    distanceUnit: d.distanceUnit,
    weightUnit: d.weightUnit,
    temperatureUnit: d.temperatureUnit,
    gpsFormat: d.gpsFormat,
    emergencyNumbers: JSON.stringify(d.emergencyNumbers),
  };
}

export default function CountryFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  existingRecords = [],
  editingId,
  saveError,
}: Props) {
  const { data: bundle, isLoading: referenceLoading, isError: referenceError } = useQuery<CountryReferenceBundle>({
    queryKey: ['country-reference'],
    queryFn: getCountryReference,
    staleTime: Infinity,
    enabled: open,
  });
  const { data: timezoneMasters, isLoading: timezonesLoading } = useQuery({
    queryKey: ['masters', 'timezones', 'country-options'],
    queryFn: () => getMasters('timezones', { limit: 2000, page: 1 }),
    enabled: open,
    staleTime: 60_000,
  });
  const timezoneStatus = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const row of (timezoneMasters?.data ?? []) as Array<{ isActive?: boolean; metadata?: Record<string, unknown> }>) {
      const id = str(row.metadata?.ianaId);
      if (id) map.set(id, row.isActive !== false);
    }
    return map;
  }, [timezoneMasters]);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [prefilledFromDefaults, setPrefilledFromDefaults] = useState(false);
  const [postalSample, setPostalSample] = useState('');
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const meta = form.metadata;
  const reference = useMemo(
    () => bundle?.countries.find((c) => c.code === str(form.code).toUpperCase()),
    [bundle, form.code],
  );
  const suggestedSortOrder = useMemo(
    () => nextCountrySortOrder(existingRecords, editingId),
    [existingRecords, editingId],
  );
  const takenCodes = useMemo(
    () => new Map(existingRecords.filter((r) => r._id !== editingId).map((r) => [str(r.code).toUpperCase(), r])),
    [existingRecords, editingId],
  );

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    if (editing) {
      const record = existingRecords.find((r) => r._id === editingId);
      if (!bundle || !record || str(record.code).toUpperCase() !== str(form.code).toUpperCase()) return;
    }
    initializedFor.current = key;
    setTouched(new Set());
    setPostalSample('');
    setPrefilledFromDefaults(false);
    if (!editing) {
      onChange({ ...form, isActive: true, sortOrder: suggestedSortOrder });
      return;
    }
    const ref = bundle?.countries.find((c) => c.code === str(form.code).toUpperCase());
    if (!ref) return;
    const defaults = metadataFromReference(ref);
    const missing = Object.entries(defaults).filter(([k, v]) => str(meta[k]) === '' && str(v) !== '');
    if (missing.length) {
      onChange({ ...form, metadata: { ...meta, ...Object.fromEntries(missing) } });
      setPrefilledFromDefaults(true);
    }
  }, [open, editing, editingId, bundle, form, meta, onChange, suggestedSortOrder, existingRecords]);

  useEffect(() => {
    if (!open || editing || touched.has('sortOrder') || initializedFor.current !== 'create') return;
    if (form.sortOrder !== suggestedSortOrder) onChange({ ...form, sortOrder: suggestedSortOrder });
  }, [open, editing, touched, suggestedSortOrder, form, onChange]);

  const savedTimezone = useMemo(
    () => str(existingRecords.find((r) => r._id === editingId)?.metadata?.timezone),
    [existingRecords, editingId],
  );
  const timezoneMasterNote = (tz: string) => {
    if (timezonesLoading || !timezoneMasters) return '';
    if (!timezoneStatus.has(tz)) return 'not in Timezone master';
    return timezoneStatus.get(tz) ? '' : 'inactive in Timezone master';
  };
  const errors = useMemo(() => {
    const base = validateCountryForm(
      { code: form.code, name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { bundle, reference, existing: existingRecords, editingId },
    );
    const tz = str(meta.timezone);
    if (!base.timezone && tz && tz !== savedTimezone && timezoneMasters && !timezonesLoading) {
      if (!timezoneStatus.has(tz)) base.timezone = `${tz} is not in the Timezone master — add it there first`;
      else if (!timezoneStatus.get(tz)) base.timezone = `${tz} is inactive in the Timezone master — activate it first`;
    }
    return base;
  }, [form, meta, bundle, reference, existingRecords, editingId, savedTimezone, timezoneMasters, timezonesLoading, timezoneStatus]);
  const warnings = useMemo(() => countryConsistencyWarnings(meta, reference), [meta, reference]);
  const blocking = useMemo(() => {
    const labels = new Map<string, string>();
    for (const key of Object.keys(errors)) {
      const base = key.startsWith('emergency.') ? 'emergencyNumbers' : key;
      labels.set(base, COUNTRY_FIELD_LABELS[base] || base);
    }
    return [...labels.entries()];
  }, [errors]);
  const isValid = blocking.length === 0;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) ? errors[key] : undefined);
  const setTop = (patch: Partial<MasterFormState>, ...keys: string[]) => {
    onChange({ ...form, ...patch });
    touch(...keys);
  };
  const setMeta = (patch: CountryMeta, ...keys: string[]) => {
    onChange({ ...form, metadata: { ...meta, ...patch } });
    touch(...(keys.length ? keys : Object.keys(patch)));
  };

  const applyCountry = (ref: CountryReference | null) => {
    if (!ref) {
      onChange({ ...form, code: '', name: '', metadata: {} });
      touch('country');
      return;
    }
    onChange({ ...form, code: ref.code, name: ref.name, metadata: { ...metadataFromReference(ref), flagUrl: str(meta.flagUrl) } });
    touch('country');
    setPostalSample('');
  };

  const resetLocaleDefaults = () => {
    if (!reference) return;
    onChange({ ...form, metadata: { ...meta, ...metadataFromReference(reference), flagUrl: str(meta.flagUrl) } });
  };

  const focusField = (key: string) => {
    touch(key);
    const el = contentRef.current?.querySelector(`[data-field="${key}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const currencyOptions = useMemo(() => {
    const all = bundle?.currencies || [];
    const local = reference?.currencies || [];
    return [...local, ...all.filter((c) => !local.includes(c))];
  }, [bundle, reference]);
  const languageOptions = useMemo(() => {
    const all = bundle?.languages || [];
    const local = reference?.languages || [];
    return [
      ...local.map((code) => all.find((l) => l.code === code)).filter(Boolean),
      ...all.filter((l) => !local.includes(l.code)),
    ] as CountryReferenceBundle['languages'];
  }, [bundle, reference]);
  const languageLabel = (code: string) => {
    const lang = bundle?.languages.find((l) => l.code === code);
    return lang ? `${lang.name}${lang.native && lang.native !== lang.name ? ` (${lang.native})` : ''} · ${code}` : code;
  };

  const weekendDays = parseJsonArray<number>(meta.weekendDays) || [];
  const additionalLanguages = (parseJsonArray<string>(meta.additionalLanguages) || []).filter((c) => typeof c === 'string');
  const addressFormat = (parseJsonArray<string>(meta.addressFormat) || []).filter((t) => typeof t === 'string');
  const emergency = (parseJsonObject(meta.emergencyNumbers) || {}) as Record<string, string>;
  const measurement = str(meta.measurementSystem) as keyof typeof UNITS_BY_SYSTEM | '';
  const taxSystem = str(meta.taxSystem);
  const taxSupportsToggle = taxSystem === 'gst' || taxSystem === 'vat';
  const postalPattern = str(meta.postalCodePattern);
  const postalMatches = (() => {
    if (!postalPattern || !postalSample.trim() || errors.postalCodePattern) return null;
    try {
      return new RegExp(postalPattern).test(postalSample.trim());
    } catch {
      return null;
    }
  })();
  const lat = Number(reference?.latitude);
  const lng = Number(reference?.longitude);
  const gpsExample = Number.isFinite(lat) && Number.isFinite(lng)
    ? (str(meta.gpsFormat) === 'dms'
      ? `${formatDms(lat, 'N', 'S')} ${formatDms(lng, 'E', 'W')}`
      : `${lat.toFixed(4)}, ${lng.toFixed(4)}`)
    : '';

  const selectField = (
    key: string,
    options: Array<{ value: string; label: string; disabled?: boolean }>,
    opts: { required?: boolean; helper?: string; onSelect?: (value: string) => void; disabled?: boolean } = {},
  ) => (
    <TextField
      select
      fullWidth
      size="small"
      data-field={key}
      label={COUNTRY_FIELD_LABELS[key]}
      required={opts.required}
      disabled={opts.disabled}
      value={str(meta[key])}
      onChange={(e) => (opts.onSelect ? opts.onSelect(e.target.value) : setMeta({ [key]: e.target.value }))}
      onBlur={() => touch(key)}
      error={!!errorFor(key)}
      helperText={errorFor(key) || opts.helper}
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
    >
      <MenuItem value="" disabled={opts.required}>
        <Typography component="span" color="text.secondary">— Select —</Typography>
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</MenuItem>
      ))}
    </TextField>
  );

  const unitOptions = (key: 'distanceUnit' | 'weightUnit' | 'temperatureUnit', all: Array<{ value: string; label: string }>) => {
    const allowed = measurement ? (UNITS_BY_SYSTEM[measurement][key] as readonly string[]) : null;
    return all.map((o) => ({ ...o, disabled: !!allowed && !allowed.includes(o.value) }));
  };

  const countryLocked = editing;
  const hasCountry = !!str(form.code);

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Country`}
      subtitle="Pick a country — ISO codes, dialling code, currency, timezone and locale defaults fill in automatically and can be overridden."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={editing ? 'Update' : 'Create'}
      loading={loading}
      disableSubmit={!isValid || referenceLoading}
      submitHint={!isValid ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${editing ? 'update' : 'create'} — see the checklist at the top` : undefined}
      width={720}
      contentRef={contentRef}
    >
      {referenceError && (
        <Alert severity="error" sx={{ mb: 2 }}>Could not load country reference data. Close and reopen the form to retry.</Alert>
      )}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {!isValid && (
        <Alert severity={hasCountry ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {hasCountry
              ? `Complete these to enable ${editing ? 'Update' : 'Create'}:`
              : 'Start by selecting a country — most fields below fill in automatically.'}
          </Typography>
          {hasCountry && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip key={key} size="small" label={label} color="warning" variant="outlined" onClick={() => focusField(key)} />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {prefilledFromDefaults && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setPrefilledFromDefaults(false)}>
          Empty fields were pre-filled from {reference?.name} defaults — review them before saving.
        </Alert>
      )}

      <FormSection title="Country" divider>
        <FormGrid>
          <Grid item xs={12} data-field="country">
            <Autocomplete
              options={bundle?.countries || []}
              loading={referenceLoading}
              disabled={countryLocked}
              value={reference || null}
              onChange={(_, value) => applyCountry(value)}
              onBlur={() => touch('country')}
              getOptionLabel={(o) => `${o.flag} ${o.name} (${o.code})`}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              getOptionDisabled={(o) => takenCodes.has(o.code)}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => o.name.toLowerCase().includes(q)
                  || o.code.toLowerCase() === q
                  || o.iso3.toLowerCase() === q
                  || o.native.toLowerCase().includes(q));
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.code}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <span>{o.flag}</span>
                    <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.code} · {o.phoneCode}</Typography>
                    {takenCodes.has(o.code) && <Chip size="small" label="Already added" />}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Country"
                  placeholder="Search by name, ISO2 or ISO3 code"
                  error={!!errorFor('country')}
                  helperText={errorFor('country')
                    || (countryLocked
                      ? 'Country cannot be changed after creation'
                      : 'Countries already in the master are disabled to prevent duplicates')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Display name"
              value={form.name}
              onChange={(e) => setTop({ name: e.target.value }, 'name')}
              onBlur={() => touch('name')}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Filled from the selected country — you can shorten it (e.g. UAE)'}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="code">
            <TextField
              fullWidth
              size="small"
              required
              label="Code"
              value={form.code}
              error={!!errorFor('code')}
              helperText={errorFor('code') || 'Auto-generated — ISO 3166-1 alpha-2 code of the selected country'}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="Auto" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                setTop({ sortOrder: raw === '' ? 0 : Number(raw) }, 'sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errors.sortOrder && (touched.has('sortOrder') || /already used/.test(errors.sortOrder))}
              helperText={
                (touched.has('sortOrder') || /already used/.test(errors.sortOrder || '')) && errors.sortOrder
                  ? errors.sortOrder
                  : `Must be unique — next available is ${suggestedSortOrder}`
              }
              inputProps={{ min: 1, max: 9999, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setTop({ sortOrder: suggestedSortOrder }, 'sortOrder')}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Countries list to activate or deactivate this country.'
                : 'New countries are created as Active. Use the status toggle in the list to deactivate.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => setTop({ description: limitConsecutiveRepeatedCharacters(e.target.value) }, 'description')}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `Optional · repeated characters (aaaa) or repeated words are not allowed · ${form.description.length}/500`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      {hasCountry && (
        <>
          <FormSection
            title="Identity & contact"
            divider
            action={reference ? (
              <Button size="small" startIcon={<RestartAlt />} onClick={resetLocaleDefaults}>
                Reset all to {reference.name} defaults
              </Button>
            ) : undefined}
          >
            <FormGrid>
              <Grid item xs={12} sm={6} data-field="iso3Code">
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="ISO3 Code"
                  value={str(meta.iso3Code)}
                  onChange={(e) => setMeta({ iso3Code: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) })}
                  onBlur={() => touch('iso3Code')}
                  error={!!errorFor('iso3Code')}
                  helperText={errorFor('iso3Code') || `ISO 3166-1 alpha-3${reference ? ` — ${reference.name} is ${reference.iso3}` : ''}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="phoneCode">
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Phone Code"
                  value={str(meta.phoneCode)}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^\d-]/g, '').slice(0, 9);
                    setMeta({ phoneCode: digits ? `+${digits}` : '' });
                  }}
                  onBlur={() => touch('phoneCode')}
                  error={!!errorFor('phoneCode')}
                  helperText={errorFor('phoneCode') || `International dialling code shown before phone numbers${reference ? ` (e.g. ${reference.phoneCode} 98765 43210)` : ''}`}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Chip size="small" label={`${reference?.flag || ''} ${str(form.code)}`.trim()} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="phoneLength">
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Length"
                  placeholder="10"
                  value={str(meta.phoneLength)}
                  onChange={(e) => setMeta({ phoneLength: e.target.value.replace(/[^\d-]/g, '').slice(0, 5) })}
                  onBlur={() => touch('phoneLength')}
                  error={!!errorFor('phoneLength')}
                  helperText={errorFor('phoneLength') || 'Local number digits after the dial code — e.g. 10, or a range like 10-11. Used by app login and admin phone fields.'}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="nationality">
                <TextField
                  fullWidth
                  size="small"
                  label="Nationality Label"
                  value={str(meta.nationality)}
                  onChange={(e) => setMeta({ nationality: e.target.value.slice(0, 50) })}
                  onBlur={() => touch('nationality')}
                  error={!!errorFor('nationality')}
                  helperText={errorFor('nationality') || `Demonym shown on profiles${reference?.defaults.nationality ? ` — e.g. ${reference.defaults.nationality}` : ''}`}
                />
              </Grid>
              <Grid item xs={12} data-field="flagUrl">
                <FormFileUpload
                  label="Flag"
                  value={str(meta.flagUrl)}
                  onChange={(url) => setMeta({ flagUrl: url })}
                  folder="masters/countries/flags"
                  accept="image/png,image/jpeg,image/webp"
                  maxSizeKb={512}
                  imageConstraints={{ maxWidth: 640, maxHeight: 480, minWidth: 16, minHeight: 16 }}
                  helperText={`PNG, JPG or WEBP · max 512 KB${reference ? ` · if empty, the ${reference.flag} emoji flag is used` : ''}`}
                />
              </Grid>
            </FormGrid>
          </FormSection>

          <FormSection title="Currency & language" divider>
            <FormGrid>
              <Grid item xs={12} sm={6} data-field="defaultCurrency">
                <Autocomplete
                  options={currencyOptions}
                  value={str(meta.defaultCurrency) || null}
                  onChange={(_, value) => {
                    const next = value || '';
                    const patch: CountryMeta = { defaultCurrency: next };
                    if (next && str(meta.secondaryCurrency) === next) patch.secondaryCurrency = '';
                    setMeta(patch, 'defaultCurrency');
                  }}
                  onBlur={() => touch('defaultCurrency')}
                  getOptionLabel={(c) => `${c} — ${currencyName(c)}`}
                  groupBy={(c) => (reference?.currencies.includes(c) ? `Used in ${reference.name}` : 'Other currencies')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label="Default Currency"
                      error={!!errorFor('defaultCurrency')}
                      helperText={errorFor('defaultCurrency') || 'ISO 4217 currency used for prices and payouts'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="secondaryCurrency">
                <Autocomplete
                  options={currencyOptions.filter((c) => c !== str(meta.defaultCurrency))}
                  value={str(meta.secondaryCurrency) || null}
                  onChange={(_, value) => setMeta({ secondaryCurrency: value || '' })}
                  onBlur={() => touch('secondaryCurrency')}
                  getOptionLabel={(c) => `${c} — ${currencyName(c)}`}
                  groupBy={(c) => (reference?.currencies.includes(c) ? `Used in ${reference.name}` : 'Other currencies')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Secondary Currency"
                      error={!!errorFor('secondaryCurrency')}
                      helperText={errorFor('secondaryCurrency') || 'Optional · must differ from the default currency'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="defaultLanguage">
                <Autocomplete
                  options={languageOptions}
                  value={languageOptions.find((l) => l.code === str(meta.defaultLanguage)) || null}
                  onChange={(_, value) => {
                    const code = value?.code || '';
                    const patch: CountryMeta = { defaultLanguage: code };
                    if (code && additionalLanguages.includes(code)) {
                      patch.additionalLanguages = JSON.stringify(additionalLanguages.filter((c) => c !== code));
                    }
                    setMeta(patch, 'defaultLanguage');
                  }}
                  onBlur={() => touch('defaultLanguage')}
                  getOptionLabel={(l) => languageLabel(l.code)}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  groupBy={(l) => (reference?.languages.includes(l.code) ? `Spoken in ${reference.name}` : 'Other languages')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label="Default Language"
                      error={!!errorFor('defaultLanguage')}
                      helperText={errorFor('defaultLanguage') || 'Pre-selected from the country — override if needed'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} data-field="additionalLanguages">
                <Autocomplete
                  multiple
                  options={languageOptions.filter((l) => l.code !== str(meta.defaultLanguage))}
                  value={additionalLanguages
                    .map((code) => languageOptions.find((l) => l.code === code))
                    .filter(Boolean) as CountryReferenceBundle['languages']}
                  onChange={(_, value) => setMeta({ additionalLanguages: JSON.stringify(value.map((l) => l.code)) })}
                  getOptionLabel={(l) => languageLabel(l.code)}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  renderTags={(value, getTagProps) => value.map((l, i) => (
                    <Chip {...getTagProps({ index: i })} key={l.code} size="small" label={`${l.name} · ${l.code}`} />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Additional Languages"
                      error={!!errorFor('additionalLanguages')}
                      helperText={errorFor('additionalLanguages') || 'Optional · saved as a JSON array of language codes'}
                    />
                  )}
                />
              </Grid>
            </FormGrid>
          </FormSection>

          <FormSection title="Date, time & calendar" divider>
            <FormGrid>
              <Grid item xs={12} sm={6} data-field="timezone">
                {selectField(
                  'timezone',
                  (reference?.timezones || (str(meta.timezone) ? [str(meta.timezone)] : [])).map((tz) => {
                    const note = timezoneMasterNote(tz);
                    return {
                      value: tz,
                      label: `${tz.replace(/_/g, ' ')} (${timezoneOffset(tz)})${note ? ` — ${note}` : ''}`,
                      disabled: !!note && tz !== savedTimezone,
                    };
                  }),
                  {
                    required: true,
                    helper: reference && reference.timezones.length > 1
                      ? `${reference.timezones.length} timezones in ${reference.name} — only active Timezone master entries can be picked`
                      : 'From the Timezone master · used for schedules and reports',
                  },
                )}
              </Grid>
              <Grid item xs={12} sm={6} data-field="firstDayOfWeek">
                {selectField('firstDayOfWeek', [
                  { value: '0', label: 'Sunday' },
                  { value: '1', label: 'Monday' },
                  { value: '6', label: 'Saturday' },
                ], { required: true })}
              </Grid>
              <Grid item xs={12} data-field="weekendDays">
                <Typography variant="body2" fontWeight={600} gutterBottom>Weekend Days</Typography>
                <ToggleButtonGroup
                  size="small"
                  value={weekendDays}
                  onChange={(_, value: number[]) => setMeta({ weekendDays: JSON.stringify([...value].sort((a, b) => a - b)) })}
                >
                  {WEEKDAYS.map((day, index) => (
                    <ToggleButton key={day} value={index} sx={{ px: 1.5 }}>{day}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color={errorFor('weekendDays') ? 'error' : 'text.secondary'} display="block" mt={0.5}>
                  {errorFor('weekendDays') || `Saved as ${JSON.stringify(weekendDays)} (0 = Sunday … 6 = Saturday) · up to 3 days`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} data-field="dateFormat">
                {selectField('dateFormat', [
                  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
                ], { required: true, helper: str(meta.dateFormat) ? `Today: ${datePreview(str(meta.dateFormat))}` : undefined })}
              </Grid>
              <Grid item xs={12} sm={4} data-field="timeFormat">
                {selectField('timeFormat', [
                  { value: '12h', label: '12 Hour' },
                  { value: '24h', label: '24 Hour' },
                ], { required: true, helper: str(meta.timeFormat) === '12h' ? 'e.g. 02:30 PM' : str(meta.timeFormat) ? 'e.g. 14:30' : undefined })}
              </Grid>
              <Grid item xs={12} sm={4} data-field="numberFormat">
                {selectField('numberFormat', [
                  { value: 'en-IN', label: 'Indian' },
                  { value: 'en-US', label: 'US / International' },
                  { value: 'de-DE', label: 'European' },
                ], { required: true, helper: str(meta.numberFormat) ? `e.g. ${numberPreview(str(meta.numberFormat))}` : undefined })}
              </Grid>
            </FormGrid>
          </FormSection>

          <FormSection title="Tax, invoicing & address" divider>
            <FormGrid>
              <Grid item xs={12} sm={6} data-field="taxSystem">
                {selectField(
                  'taxSystem',
                  Object.entries(TAX_LABELS).map(([value, label]) => ({ value, label })),
                  {
                    required: true,
                    helper: reference ? `Typical for ${reference.name}: ${TAX_LABELS[reference.defaults.taxSystem]}` : undefined,
                    onSelect: (value) => setMeta({
                      taxSystem: value,
                      gstVatEnabled: value === 'gst' || value === 'vat',
                    }, 'taxSystem', 'gstVatEnabled'),
                  },
                )}
              </Grid>
              <Grid item xs={12} sm={6} data-field="gstVatEnabled">
                <FormControlLabel
                  control={(
                    <Switch
                      checked={taxSupportsToggle && (meta.gstVatEnabled === true || meta.gstVatEnabled === 'true')}
                      disabled={!taxSupportsToggle}
                      onChange={(e) => setMeta({ gstVatEnabled: e.target.checked })}
                    />
                  )}
                  label={taxSystem === 'gst' ? 'GST Enabled' : taxSystem === 'vat' ? 'VAT Enabled' : 'GST/VAT Enabled'}
                />
                <Typography variant="caption" color={errorFor('gstVatEnabled') ? 'error' : 'text.secondary'} display="block">
                  {errorFor('gstVatEnabled') || (taxSupportsToggle
                    ? `Apply ${TAX_LABELS[taxSystem]} on orders and invoices`
                    : 'Available only when Tax System is GST or VAT')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} data-field="invoicePrefix">
                <TextField
                  fullWidth
                  size="small"
                  label="Invoice Prefix"
                  value={str(meta.invoicePrefix)}
                  onChange={(e) => setMeta({ invoicePrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 10) })}
                  onBlur={() => touch('invoicePrefix')}
                  error={!!errorFor('invoicePrefix')}
                  helperText={errorFor('invoicePrefix') || `2–10 characters (A–Z, 0–9, -), unique per country · e.g. ${str(meta.invoicePrefix) || 'INV-IN'}-000123`}
                />
              </Grid>
              <Grid item xs={12} data-field="postalCodePattern">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Postal Code Format (regular expression)"
                    value={postalPattern}
                    onChange={(e) => setMeta({ postalCodePattern: e.target.value.slice(0, 200) })}
                    onBlur={() => touch('postalCodePattern')}
                    error={!!errorFor('postalCodePattern') || !!errors.postalCodePattern}
                    helperText={errors.postalCodePattern
                      || (postalPattern
                        ? 'Used to validate postal codes in customer addresses'
                        : `Optional${reference && !reference.defaults.addressFormat.includes('postalCode') ? ` — ${reference.name} does not use postal codes` : ' — leave empty to skip postal code validation'}`)}
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                  <TextField
                    size="small"
                    label="Test a postal code"
                    value={postalSample}
                    onChange={(e) => setPostalSample(e.target.value)}
                    disabled={!postalPattern || !!errors.postalCodePattern}
                    sx={{ minWidth: { sm: 220 } }}
                    helperText={postalMatches == null ? 'Type a sample to check the pattern' : postalMatches ? 'Valid postal code' : 'Does not match the pattern'}
                    InputProps={{
                      endAdornment: postalMatches == null ? undefined : (
                        <InputAdornment position="end">
                          {postalMatches ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="error" fontSize="small" />}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} data-field="addressFormat">
                <Autocomplete
                  multiple
                  options={bundle?.addressFormatTokens || []}
                  value={addressFormat}
                  onChange={(_, value) => setMeta({ addressFormat: JSON.stringify(value) })}
                  onBlur={() => touch('addressFormat')}
                  getOptionLabel={(t) => ADDRESS_TOKEN_LABELS[t] || t}
                  filterSelectedOptions
                  renderTags={(value, getTagProps) => value.map((t, i) => (
                    <Chip {...getTagProps({ index: i })} key={t} size="small" label={`${i + 1}. ${ADDRESS_TOKEN_LABELS[t] || t}`} />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Address Format"
                      error={!!errorFor('addressFormat')}
                      helperText={errorFor('addressFormat') || 'Address fields in display order (saved as a JSON array) — must include Address line 1 and City'}
                    />
                  )}
                />
              </Grid>
            </FormGrid>
          </FormSection>

          <FormSection title="Units & coordinates" divider>
            <FormGrid>
              <Grid item xs={12} sm={6} data-field="measurementSystem">
                {selectField('measurementSystem', [
                  { value: 'metric', label: 'Metric' },
                  { value: 'imperial', label: 'Imperial' },
                ], {
                  required: true,
                  helper: 'Changing the system switches distance, weight and temperature units to match',
                  onSelect: (value) => {
                    const system = value as keyof typeof UNITS_BY_SYSTEM;
                    const units = UNITS_BY_SYSTEM[system];
                    const keepTemp = units && (units.temperatureUnit as readonly string[]).includes(str(meta.temperatureUnit));
                    setMeta({
                      measurementSystem: value,
                      ...(units ? {
                        distanceUnit: units.distanceUnit[0],
                        weightUnit: units.weightUnit[0],
                        temperatureUnit: keepTemp ? str(meta.temperatureUnit) : units.temperatureUnit[0],
                      } : {}),
                    }, 'measurementSystem', 'distanceUnit', 'weightUnit', 'temperatureUnit');
                  },
                })}
              </Grid>
              <Grid item xs={12} sm={6} data-field="gpsFormat">
                {selectField('gpsFormat', [
                  { value: 'decimal', label: 'Decimal Degrees' },
                  { value: 'dms', label: 'Degrees, Minutes, Seconds (DMS)' },
                ], { required: true, helper: gpsExample ? `e.g. ${gpsExample}` : undefined })}
              </Grid>
              <Grid item xs={12} sm={4} data-field="distanceUnit">
                {selectField('distanceUnit', unitOptions('distanceUnit', [
                  { value: 'km', label: 'Kilometres (KM)' },
                  { value: 'mi', label: 'Miles' },
                ]), { required: true, helper: measurement ? `Allowed for ${measurement}` : 'Select a measurement system first', disabled: !measurement })}
              </Grid>
              <Grid item xs={12} sm={4} data-field="weightUnit">
                {selectField('weightUnit', unitOptions('weightUnit', [
                  { value: 'kg', label: 'Kilograms (KG)' },
                  { value: 'lb', label: 'Pounds (LB)' },
                ]), { required: true, helper: measurement ? `Allowed for ${measurement}` : undefined, disabled: !measurement })}
              </Grid>
              <Grid item xs={12} sm={4} data-field="temperatureUnit">
                {selectField('temperatureUnit', unitOptions('temperatureUnit', [
                  { value: 'celsius', label: 'Celsius (°C)' },
                  { value: 'fahrenheit', label: 'Fahrenheit (°F)' },
                ]), { required: true, helper: measurement ? `Allowed for ${measurement}` : undefined, disabled: !measurement })}
              </Grid>
            </FormGrid>
          </FormSection>

          <FormSection title="Emergency numbers" divider>
            <FormGrid>
              {(bundle?.emergencyServiceKeys || ['police', 'ambulance', 'fire']).map((service) => (
                <Grid item xs={12} sm={4} key={service} data-field="emergencyNumbers">
                  <TextField
                    fullWidth
                    size="small"
                    label={service.charAt(0).toUpperCase() + service.slice(1)}
                    value={str(emergency[service])}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                      const next = { ...emergency, [service]: digits };
                      if (!digits) delete next[service];
                      setMeta({ emergencyNumbers: JSON.stringify(next) }, `emergency.${service}`);
                    }}
                    onBlur={() => touch(`emergency.${service}`)}
                    error={!!errorFor(`emergency.${service}`)}
                    helperText={errorFor(`emergency.${service}`) || 'Optional · 2–6 digits'}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Saved as JSON: {JSON.stringify(emergency)}
                </Typography>
              </Grid>
            </FormGrid>
          </FormSection>

          {warnings.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1 }}
              action={reference ? (
                <Button size="small" color="inherit" onClick={resetLocaleDefaults}>Use defaults</Button>
              ) : undefined}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>Locale consistency checks</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {warnings.map((w) => <Box component="li" key={w} sx={{ typography: 'body2' }}>{w}</Box>)}
              </Box>
            </Alert>
          )}
        </>
      )}
      <Box sx={{ height: 16 }} aria-hidden />
    </FormDrawer>
  );
}
